import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { ToastController } from '@ionic/angular';
import { finalize } from 'rxjs';

import { GroupManagementService } from '@services/group-management.service';
import { PanelHostService } from '@services/panel-host.service';
import { rosterParticipantFromPanelContext } from '@services/panel-store';
import { TripService } from '@services/trip.service';
import { RosterParticipant } from '../../../models';
import { ContentContainerComponent } from '../../ui/content-container/content-container.component';
import { PanelBodyComponent } from '../../ui/panel-body/panel-body.component';

@Component({
  selector: 'app-trip-invitation',
  imports: [TranslocoDirective, ContentContainerComponent, PanelBodyComponent],
  templateUrl: './trip-invitation.component.html',
  styleUrls: ['./trip-invitation.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TripInvitationComponent {
  private readonly panelHost = inject(PanelHostService);
  private readonly tripService = inject(TripService);
  private readonly groupManagementService = inject(GroupManagementService);
  private readonly transloco = inject(TranslocoService);
  private readonly toastController = inject(ToastController);

  readonly submitting = signal(false);

  readonly participants = computed((): RosterParticipant[] => {
    const context = this.panelHost.rosterPanelContext();
    if (context?.participants?.length) {
      return context.participants;
    }
    const participant = rosterParticipantFromPanelContext(context);
    if (participant) {
      return [participant];
    }
    return [];
  });

  readonly isBulk = computed(() => this.participants().length > 1);

  constructor() {
    effect(() => {
      if (this.panelHost.activePanel() !== 'trip-invitation') {
        this.submitting.set(false);
      }
    });
  }

  cancel(): void {
    this.panelHost.dismiss();
  }

  confirm(): void {
    const participants = this.participants();
    const trip = this.tripService.currentTrip.value();
    if (participants.length === 0 || !trip || this.submitting()) {
      return;
    }

    this.submitting.set(true);
    this.groupManagementService
      .resendInvitation(trip.id, participants.map((p) => p.id))
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe({
        next: async () => {
          const toast = await this.toastController.create({
            message: this.transloco.translate(
              this.isBulk()
                ? marker('group-management.panels.trip-invitation.success-bulk')
                : marker('group-management.panels.trip-invitation.success'),
              this.isBulk()
                ? { count: participants.length }
                : { name: participants[0].firstname },
            ),
            duration: 4000,
            color: 'success',
          });
          await toast.present();
          this.panelHost.dismiss();
        },
        error: async () => {
          const toast = await this.toastController.create({
            message: this.transloco.translate(
              this.isBulk()
                ? marker('group-management.panels.trip-invitation.error-bulk')
                : marker('group-management.panels.trip-invitation.error'),
              this.isBulk()
                ? { count: participants.length }
                : { name: participants[0].firstname },
            ),
            duration: 4000,
            color: 'danger',
          });
          await toast.present();
        },
      });
  }
}
